import PropTypes from "prop-types";
import { Box, Grid, Typography } from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";

import PageLayout from "examples/LayoutContainers/PageLayout";
import MDBox from "components/MDBox";
import logo from "assets/images/sidenav_logo.svg";

const featureList = [
  {
    icon: <PeopleAltOutlinedIcon sx={{ color: "#1E40AF", fontSize: "22px" }} />,
    title: "Partner First",
    desc: "Tools and support to help you succeed",
  },
  {
    icon: <DescriptionOutlinedIcon sx={{ color: "#1E40AF", fontSize: "22px" }} />,
    title: "Easy Access",
    desc: "Manage leads, applications and track status",
  },
  {
    icon: <BarChartOutlinedIcon sx={{ color: "#1E40AF", fontSize: "22px" }} />,
    title: "Better Together",
    desc: "Insights and resources to grow your business",
  },
];

function PartnerAuthLayout({ children }) {
  return (
    <PageLayout>
      <Box
        sx={{
          minHeight: "100vh",
          width: "100vw",
          backgroundColor: "#F3F6FB",
          position: "relative",
          overflowX: "hidden",
          overflowY: "auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Subtle decorative background gradient glows spanning edge-to-edge */}
        <Box
          sx={{
            position: "absolute",
            top: "-6%",
            left: "-4%",
            width: "520px",
            height: "520px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(219, 234, 254, 0.6) 0%, rgba(243, 246, 251, 0) 70%)",
            filter: "blur(55px)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "-6%",
            right: "-4%",
            width: "560px",
            height: "560px",
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(224, 242, 254, 0.6) 0%, rgba(243, 246, 251, 0) 70%)",
            filter: "blur(55px)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        {/* Seamless Inner Content Wrapper: limits max distance between columns on wide screens without any visible container frame */}
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", md: "1140px", lg: "1220px", xl: "1260px" },
            mx: "auto",
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 2.5, sm: 3, md: 4, lg: 5 },
            py: { xs: 3, md: 4 },
            position: "relative",
            zIndex: 1,
          }}
        >
          <Grid
            container
            spacing={{ xs: 4, md: 4, lg: 5 }}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            {/* Left Column: Branding & Features */}
            <Grid
              item
              xs={12}
              md={6}
              lg={6.2}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                position: "relative",
                pr: { md: 2, lg: 3 },
              }}
            >
              {/* Top Row: Brand Logo & Decorative Dot Grid aligned horizontally */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  mb: { xs: 3.5, md: 4 },
                  pr: { xs: 0, sm: 2, md: 3 },
                }}
              >
                {/* Brand Logo */}
                <MDBox
                  component="img"
                  src={logo}
                  alt="Finvibes"
                  sx={{
                    width: { xs: "150px", sm: "175px" },
                    height: "auto",
                    display: "block",
                  }}
                />

                {/* Decorative Dot Grid */}
                <Box
                  sx={{
                    pointerEvents: "none",
                    display: { xs: "none", sm: "block" },
                    lineHeight: 0,
                  }}
                >
                  <svg width="104" height="56" viewBox="0 0 104 56" fill="none">
                    {[0, 1, 2, 3, 4, 5].map((col) =>
                      [0, 1, 2, 3].map((row) => (
                        <circle
                          key={`${col}-${row}`}
                          cx={4 + col * 18}
                          cy={2.5 + row * 16}
                          r="2.5"
                          fill="#94A3B8"
                          opacity="0.38"
                        />
                      ))
                    )}
                  </svg>
                </Box>
              </Box>

              {/* Main Tagline & Content */}
              <Box>
                {/* Hero Headlines */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{
                      fontSize: { xs: "28px", sm: "34px", md: "38px", lg: "42px" },
                      fontWeight: 800,
                      color: "#101828",
                      lineHeight: 1.15,
                      fontFamily: "Inter, sans-serif",
                      letterSpacing: "-0.025em",
                    }}
                  >
                    Your Partnership.
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "28px", sm: "34px", md: "38px", lg: "42px" },
                      fontWeight: 800,
                      color: "#101828",
                      lineHeight: 1.15,
                      fontFamily: "Inter, sans-serif",
                      letterSpacing: "-0.025em",
                    }}
                  >
                    Our Support.
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "28px", sm: "34px", md: "38px", lg: "42px" },
                      fontWeight: 800,
                      color: "#1E40AF",
                      lineHeight: 1.15,
                      fontFamily: "Inter, sans-serif",
                      letterSpacing: "-0.025em",
                    }}
                  >
                    Shared Growth.
                  </Typography>

                  {/* Blue Accent Divider */}
                  <Box
                    sx={{
                      width: "46px",
                      height: "3.5px",
                      backgroundColor: "#1E40AF",
                      borderRadius: "2px",
                      mt: 2,
                      mb: 2,
                    }}
                  />
                </Box>

                {/* Description */}
                <Typography
                  sx={{
                    fontSize: { xs: "13.5px", md: "14px" },
                    color: "#64748B",
                    lineHeight: 1.6,
                    maxWidth: "440px",
                    mb: { xs: 3.5, md: 4 },
                  }}
                >
                  Welcome to the Finvibes Partner Portal. Access resources, manage applications,
                  track performance and grow with us.
                </Typography>
              </Box>

              {/* Value Proposition Feature Items */}
              <Box sx={{ mt: { xs: 2, md: 1 } }}>
                {featureList.map((feat, index) => (
                  <Box key={feat.title}>
                    {index > 0 && (
                      <Box
                        sx={{
                          height: "1px",
                          backgroundColor: "#E2E8F0",
                          my: 1.5,
                          maxWidth: "400px",
                        }}
                      />
                    )}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: "50%",
                          backgroundColor: "#EFF6FF",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {feat.icon}
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "14px",
                            color: "#1E293B",
                            lineHeight: 1.3,
                          }}
                        >
                          {feat.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#64748B",
                            lineHeight: 1.4,
                            mt: 0.2,
                          }}
                        >
                          {feat.desc}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Right Column: Floating White Form Card */}
            <Grid
              item
              xs={12}
              md={6}
              lg={5.8}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: { xs: "center", md: "flex-end" },
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "490px",
                  backgroundColor: "#FFFFFF",
                  borderRadius: "20px",
                  p: { xs: 3, sm: 4, md: "36px 42px" },
                  boxShadow:
                    "0 15px 35px -5px rgba(15, 23, 42, 0.07), 0 2px 8px rgba(15, 23, 42, 0.03)",
                  border: "1px solid #EEF2F6",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                }}
              >
                {children}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </PageLayout>
  );
}

PartnerAuthLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

export default PartnerAuthLayout;
